import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Send, LifeBuoy, X } from "lucide-react";
import { API_BASE, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COACH } from "@/constants/testIds";
import { MicButton } from "@/components/MicButton";
import {
  AlertDialog, AlertDialogAction, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const COACH_AVATAR =
  "https://images.unsplash.com/photo-1593351799227-75df2026356b?crop=entropy&cs=srgb&fm=jpg&q=85&w=400";

const PROMPTS = [
  "I need to set a boundary with someone I love",
  "Help me prepare for a hard conversation",
  "I'm struggling with imposter syndrome at work",
  "I feel stretched thin and can't rest",
];

export default function Coach() {
  const { user, checkAuth } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [consent, setConsent] = useState(() => localStorage.getItem("sh_coach_consent") === "1");
  const [crisis, setCrisis] = useState(null);
  const scrollRef = useRef(null);

  const acceptConsent = () => { localStorage.setItem("sh_coach_consent", "1"); setConsent(true); track("coach_consent_accepted"); };

  useEffect(() => {
    fetch(`${API_BASE}/coach/history`, { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        setMessages(data || []);
        if (data?.length) setConversationId(data[0].conversation_id);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    track("coach_message_sent");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);

    try {
      const res = await fetch(`${API_BASE}/coach/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ conversation_id: conversationId, message: content }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const obj = JSON.parse(payload);
            if (obj.conversation_id) setConversationId(obj.conversation_id);
            if (obj.crisis) setCrisis(obj.crisis_message);
            if (obj.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + obj.delta };
                return copy;
              });
            }
            if (obj.error) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: obj.error };
                return copy;
              });
            }
          } catch (e) { console.debug("coach stream parse skipped:", e); }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "The coach is unavailable right now. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      if (user && !user.is_premium && user.is_trial_active) checkAuth();
    }
  };

  return (
    <>
      <AlertDialog open={!consent}>
        <AlertDialogContent data-testid="coach-consent-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Before we begin — a shared understanding</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-left">
              <span className="block">The Sisterhood+ AI Coach is designed for education, wellness, and personal growth. It is <strong>not a substitute for professional mental health care</strong>, and it does not provide diagnosis or treatment.</span>
              <span className="block">It's a supportive space to ask questions, reflect, and practice — not a therapist and not a crisis service.</span>
              <span className="block">If you're ever in crisis or in danger, call or text <a href="tel:988" className="underline font-medium">988</a> (Suicide &amp; Crisis Lifeline), or call <a href="tel:911" className="underline font-medium">911</a> in an emergency.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="coach-consent-accept" onClick={acceptConsent}
              className="bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743]">I understand — continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-5rem)] max-w-3xl mx-auto">
      <div className="flex items-center gap-4 pb-6 border-b border-[#E5DED4]">
        <img src={COACH_AVATAR} alt="Sisterhood+ Coach" className="h-12 w-12 rounded-full object-cover" />
        <div>
          <h1 className="font-display text-2xl font-semibold">Your Coach</h1>
          <p className="text-sm text-[#8A838E]">Warm, direct, and always in your corner.</p>
        </div>
        {user && !user.is_premium && user.is_trial_active && user.access && (
          <span data-testid="coach-trial-badge"
            className="ml-auto shrink-0 rounded-full bg-[#FBF4E7] border border-[#C89B55]/50 text-[#7A5A1E] text-xs font-semibold px-3 py-1.5">
            {Math.max(0, (user.access.trial_ai_limit ?? 5) - (user.access.trial_ai_used ?? 0))} of {user.access.trial_ai_limit ?? 5} free AI sessions left
          </span>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto sh-scroll py-6 space-y-5">
        {messages.length === 0 && (
          <div className="text-center py-10">
            <p className="font-display text-2xl mb-2">What's on your mind?</p>
            <p className="text-[#59535B] mb-8">This is a private, judgment-free space. Start with a prompt or your own words.</p>
            <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto">
              {PROMPTS.map((p) => (
                <button key={p} data-testid={COACH.promptPill} onClick={() => send(p)}
                  className="text-left text-sm bg-[#F7F2EA] border border-[#E5DED4] hover:border-[#C89B55] rounded-xl px-4 py-3 transition-all">
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            data-testid={COACH.message}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-2xl px-5 py-3.5 leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-[#291B2D] text-[#FCFAF6]" : "bg-[#F7F2EA] border border-[#E5DED4] text-[#252326]"
            }`}>
              {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="pt-4 border-t border-[#E5DED4]">
        {crisis && (
          <div data-testid="crisis-notice" className="mb-3 rounded-2xl border border-[#E4B9B8] bg-[#FBF0EF] p-4 flex items-start gap-3">
            <LifeBuoy className="h-5 w-5 text-[#9E3A3A] shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-[#5B2B2B] leading-relaxed">{crisis}</p>
              <div className="mt-1.5 flex gap-3">
                <a href="tel:988" className="text-sm font-semibold text-[#9E3A3A] underline">Call or text 988</a>
                <a href="tel:911" className="text-sm font-semibold text-[#9E3A3A] underline">911 (emergency)</a>
              </div>
            </div>
            <button onClick={() => setCrisis(null)} aria-label="Dismiss" data-testid="crisis-notice-close" className="p-1 rounded-full text-[#9E3A3A]/70 hover:bg-[#F3D9D8]">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-3">
          <textarea data-testid={COACH.input} value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1} placeholder="Type your message, or tap the mic to speak…"
            className="flex-1 resize-none bg-[#FCFAF6] border border-[#E5DED4] rounded-2xl px-4 py-3 max-h-32 focus:outline-none focus:ring-2 focus:ring-[#C89B55]" />
          <MicButton testid="coach-dictate" className="relative h-12 w-12 shrink-0"
            onAppend={(t) => setInput((v) => (v ? v + " " + t : t))} />
          <button data-testid={COACH.sendButton} onClick={() => send()} disabled={streaming || !input.trim()}
            aria-label="Send message"
            className="h-12 w-12 shrink-0 rounded-full bg-[#C89B55] text-[#291B2D] hover:bg-[#B38743] disabled:opacity-40 flex items-center justify-center transition-all">
            <Send className="h-5 w-5" />
          </button>
        </div>
        <p className="text-xs text-[#8A838E] mt-2 text-center">
          Your Coach offers guidance, not therapy, medical, legal, or financial advice. In crisis, call or text 988 (US).
        </p>
      </div>
    </div>
    </>
  );
}
